import * as React from "react"

import { DivergencePair } from "../divergence-pair"
import { FindingContext, FindingNumber, SeverityPill } from "../finding-parts"
import { marksInDrawOrder, pinsOnPage, unpinnedFindings } from "../document/document-layout"
import type { ReportFindingItem } from "../report-data"
import { usePdfPage } from "../use-pdf-page"

/**
 * The page width the print copy is laid out at, in CSS pixels. Paper is not
 * measured — the browser lays a page out for print without running our code —
 * so the print copy is built to a fixed width and then fills the sheet by CSS.
 */
const PRINT_PAGE_WIDTH = 720
/** Rasterised at this multiple of that width, so the label is sharp on paper. */
const PRINT_RASTER_SCALE = 2

/*
   Fixed colours, never theme tokens: the marks sit on the always-white page.
   The brand's and the "master" green's light-theme values, as on screen.
*/
const ACCENT = "#c8461a"
const DONE = "#1d6b49"

export interface PrintableLabelProps {
  /** The printed label. Empty when the review has none attached. */
  documentUrl: string
  /** Every finding, by number, with every located occurrence. */
  findings: readonly ReportFindingItem[]
}

/**
 * The checklist's and the document view's copy for paper.
 *
 * Both views place their page, marks and cards in pixels measured on screen,
 * and move them by transforms; paper is narrower and is laid out without our
 * code running, so a print of either comes out squeezed and cut. This is a
 * plain-flow version of the same content instead — the label at the sheet's
 * width with every finding's box on it, then every finding in full — shown
 * only when printing and hidden on screen.
 *
 * Rendered whenever one of those tabs is open, not on demand: a print begins
 * with no chance to render first, so the label has to be drawn already.
 */
export function PrintableLabel({ documentUrl, findings }: PrintableLabelProps): JSX.Element {
  const firstPage = useRenderedPage(documentUrl, 1)
  const pageCount = firstPage.pageCount
  const unpinned = unpinnedFindings(findings)

  return (
    <div className="hidden print:block [print-color-adjust:exact]">
      {documentUrl && (
        <section aria-label="Printed label" className="flex flex-col gap-4">
          <PrintedPage page={1} findings={findings} rendered={firstPage} />
          {Array.from({ length: Math.max(0, pageCount - 1) }, (_, i) => (
            <LaterPage key={i + 2} documentUrl={documentUrl} page={i + 2} findings={findings} />
          ))}
        </section>
      )}

      <section aria-label="Findings" className="mt-6">
        <h2 className="text-eyebrow uppercase text-text-3">
          {findings.length} divergence{findings.length === 1 ? "" : "s"}
        </h2>
        <ol className="mt-3 flex flex-col gap-3">
          {findings.map((f) => (
            <PrintedFinding key={f.id} finding={f} unpinned={unpinned.some((u) => u.id === f.id)} />
          ))}
        </ol>
      </section>
    </div>
  )
}

interface RenderedPage {
  canvasRef: React.RefObject<HTMLCanvasElement | null>
  geometry: ReturnType<typeof usePdfPage>["geometry"]
  failed: boolean
  pageCount: number
}

/** One page of the label, drawn at the print width into its own canvas. */
function useRenderedPage(documentUrl: string, page: number): RenderedPage {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const { geometry, failed } = usePdfPage(
    documentUrl,
    page,
    PRINT_PAGE_WIDTH,
    canvasRef,
    PRINT_RASTER_SCALE,
  )
  return { canvasRef, geometry, failed, pageCount: geometry?.pageCount ?? 1 }
}

/** Pages after the first: each draws itself, as the first does. */
function LaterPage({
  documentUrl,
  page,
  findings,
}: {
  documentUrl: string
  page: number
  findings: readonly ReportFindingItem[]
}): JSX.Element {
  const rendered = useRenderedPage(documentUrl, page)
  return <PrintedPage page={page} findings={findings} rendered={rendered} />
}

/**
 * A page of the label with every box on it.
 *
 * The boxes are placed in percentages of the page, never pixels, so they stay
 * on their lines at whatever width the sheet gives the page. Drawn in the
 * report's order — further occurrences under each finding's own box, then by
 * number — so a line several findings share shows the same badge as on screen.
 */
function PrintedPage({
  page,
  findings,
  rendered,
}: {
  page: number
  findings: readonly ReportFindingItem[]
  rendered: RenderedPage
}): JSX.Element {
  const { canvasRef, geometry, failed } = rendered
  const marks = geometry
    ? marksInDrawOrder(
        pinsOnPage(findings, geometry.page, geometry.pointWidth, geometry.pointHeight),
        null,
      )
    : []

  if (failed) {
    return (
      <p className="text-body-sm text-text-3">
        Page {page} of the printed label could not be drawn. Every divergence is listed below.
      </p>
    )
  }

  return (
    <figure className="break-inside-avoid">
      {/* Shrink-wrapped to the canvas, so the boxes' percentages are of the
          label itself at whatever size the sheet allows. */}
      <div className="relative mx-auto w-fit border border-border bg-white">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={`Printed label, page ${String(page)}`}
          // The bitmap is drawn at the fixed print width; the sheet decides
          // the size it is shown at. Capped in height so the first page of
          // the label fits under the masthead instead of leaving the first
          // sheet empty — both sides auto, so it keeps its proportions.
          className="block h-auto max-h-[150mm] w-auto max-w-full"
        />
        {marks.map(({ pin, box, index }) => {
          const colour = pin.finding.clientResolution === "done" ? DONE : ACCENT
          return (
            <span
              key={`${pin.finding.id}:${String(index)}`}
              aria-hidden="true"
              className="absolute rounded-[2px]"
              style={{
                left: `${String(box.x * 100)}%`,
                top: `${String(box.y * 100)}%`,
                width: `${String(box.w * 100)}%`,
                height: `${String(box.h * 100)}%`,
                backgroundColor: `${colour}1f`,
                boxShadow: `0 0 0 1.5px ${colour}`,
              }}
            >
              <span
                className="absolute -left-2 -top-2 grid size-4 place-items-center rounded-full text-[9px] font-bold text-white"
                style={{ backgroundColor: colour }}
              >
                {pin.finding.number}
              </span>
            </span>
          )
        })}
      </div>
      {(geometry?.pageCount ?? 1) > 1 && (
        <figcaption className="mt-1 text-caption text-text-3">
          Page {page} of {geometry?.pageCount}
        </figcaption>
      )}
    </figure>
  )
}

/** One finding, in full: what it is, where, and the printed wording beside the approved one. */
function PrintedFinding({
  finding,
  unpinned,
}: {
  finding: ReportFindingItem
  unpinned: boolean
}): JSX.Element {
  return (
    // Allowed to split, since one finding's quoted master can run longer than
    // a sheet, and holding it whole would leave the sheet before it empty.
    // Its heading is held together and kept with what follows, so a sheet
    // never ends on a title with nothing under it.
    <li className="rounded-lg border border-border p-3">
      <div className="break-inside-avoid break-after-avoid">
        <div className="flex flex-wrap items-center gap-2">
          <FindingNumber number={finding.number} settled={finding.clientResolution === "done"} />
          <SeverityPill severity={finding.severity} />
          {finding.occurrenceCount > 1 && (
            <span className="text-caption text-text-3">{finding.occurrenceCount}× on label</span>
          )}
          {unpinned && <span className="text-caption text-text-3">Not pinned to a location</span>}
        </div>
        <h3 className="mt-2 text-body font-semibold text-text">{finding.title}</h3>
        <FindingContext finding={finding} />
      </div>
      <DivergencePair
        className="mt-2"
        printedText={finding.printedText}
        masterText={finding.masterText}
        fallback={finding.divergence}
      />
      {finding.remediation && (
        <p className="mt-2 text-caption text-text-2">
          <span className="font-semibold text-master">What to change: </span>
          {finding.remediation}
        </p>
      )}
    </li>
  )
}
